import { FC } from 'react';

import { cn } from './utils/cn';

export type LogoProps = {
  className?: string;
};

/**
 * Eluve brand mark. Use for icons, thumbs, and small brand accents.
 *
 * Defaults to `size-8` (32px) and the brand gray fill. Override sizing and
 * color with Tailwind utilities via `className` (e.g. `size-12`, `fill-text-primary`).
 *
 * @example
 * <EluveLogo className="size-9 fill-text-primary" />
 */
export const EluveLogo: FC<LogoProps> = ({ className }) => (
  <svg
    className={cn('size-8', className)}
    fill="#3C3C3C"
    viewBox="0 0 16 16"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M8.47778 13.5C4.87778 13.5 2.5 11.3 2.5 8C2.5 4.65849 4.96667 2.5 8.61111 2.5C11.3444 2.5 13.3222 3.68302 13.3222 5.38491C13.3222 7.46038 10.5222 8.29057 7.58889 8.4566C8.03333 9.61887 8.87778 10.6151 10.3 10.6151C11.1 10.6151 11.6111 10.283 12.0556 9.99245C12.4333 9.7434 12.6778 9.61887 12.9444 9.61887C13.2556 9.61887 13.5 9.7849 13.5 10.3245C13.5 12.1094 11.3444 13.5 8.47778 13.5ZM7.12222 5.8C7.12222 6.2566 7.16667 6.83774 7.3 7.41887C8.76667 7.23207 9.27778 6.42264 9.27778 5.42641C9.27778 4.49245 8.85556 3.64151 8.16667 3.64151C7.34444 3.64151 7.12222 4.78302 7.12222 5.8Z" />
  </svg>
);

/**
 * Eluve wordmark. Use for headers, login pages, and onboarding hero areas.
 *
 * Non-square; sizing is height-driven and width follows the SVG's viewBox.
 * Defaults to `h-6` (24px tall). Override via the `h-*` family.
 *
 * @example
 * <EluveNameLogo className="h-16" />
 */
export const EluveNameLogo: FC<LogoProps> = ({ className }) => (
  <svg
    className={cn('h-6', className)}
    fill="#3C3C3C"
    viewBox="0 0 51 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M5.184 17.68C2.268 17.68 0.342 15.772 0.342 12.91C0.342 10.012 2.34 8.14 5.292 8.14C7.506 8.14 9.108 9.166 9.108 10.642C9.108 12.442 6.84 13.162 4.464 13.306C4.824 14.314 5.508 15.178 6.66 15.178C7.308 15.178 7.722 14.89 8.082 14.638C8.388 14.422 8.586 14.314 8.802 14.314C9.054 14.314 9.252 14.458 9.252 14.926C9.252 16.474 7.506 17.68 5.184 17.68ZM4.086 11.002C4.086 11.398 4.122 11.902 4.23 12.406C5.418 12.244 5.832 11.542 5.832 10.678C5.832 9.868 5.49 9.13 4.932 9.13C4.266 9.13 4.086 10.12 4.086 11.002ZM10.303 17.5C9.96103 17.5 9.74503 17.284 9.74503 16.978C9.74503 16.69 9.90703 16.564 10.069 16.438C10.267 16.276 10.411 16.132 10.411 15.826V6.574C10.411 6.268 10.249 6.124 10.069 5.98C9.90703 5.854 9.74503 5.728 9.74503 5.44C9.74503 5.134 9.97903 4.9 10.303 4.9H13.435C13.903 4.9 14.191 5.17 14.191 5.638V15.826C14.191 16.132 14.335 16.276 14.533 16.438C14.695 16.564 14.857 16.69 14.857 16.978C14.857 17.284 14.623 17.5 14.281 17.5H10.303ZM18.6983 17.68C17.1683 17.68 16.0163 16.726 16.0163 14.944V9.994C16.0163 9.688 15.8543 9.544 15.6743 9.4C15.5123 9.274 15.3503 9.148 15.3503 8.86C15.3503 8.554 15.5843 8.32 15.9083 8.32H19.0403C19.5083 8.32 19.7963 8.59 19.7963 9.058V14.854C19.7963 15.43 20.1563 15.808 20.6423 15.808C21.0023 15.808 21.3083 15.592 21.5243 15.268V9.994C21.5243 9.688 21.3803 9.544 21.2003 9.4C21.0383 9.274 20.8763 9.148 20.8763 8.86C20.8763 8.554 21.0923 8.32 21.4343 8.32H24.5663C25.0343 8.32 25.3043 8.59 25.3043 9.058V15.826C25.3043 16.132 25.4843 16.294 25.6643 16.42C25.8443 16.546 25.9703 16.654 25.9703 16.87C25.9703 17.392 25.1963 17.68 24.2423 17.68C22.8923 17.68 21.9743 17.104 21.6503 16.15C20.9303 17.104 19.8863 17.68 18.6983 17.68ZM30.8409 17.5C30.4629 17.5 30.1749 17.32 30.0309 16.96L27.1149 9.994C27.0069 9.742 26.8089 9.598 26.5569 9.454C26.2869 9.31 26.0529 9.148 26.0529 8.824C26.0529 8.536 26.2869 8.32 26.6109 8.32H31.2729C31.6149 8.32 31.8489 8.536 31.8489 8.824C31.8489 9.112 31.6509 9.274 31.4529 9.4C31.2009 9.562 31.0389 9.706 31.1469 9.994L32.5689 14.08L34.1889 9.994C34.2969 9.706 34.0629 9.562 33.7929 9.4C33.5409 9.256 33.3069 9.112 33.3069 8.824C33.3069 8.536 33.5229 8.32 33.8649 8.32H35.9709C36.3129 8.32 36.5289 8.536 36.5289 8.824C36.5289 9.148 36.3129 9.31 36.0249 9.454C35.7549 9.598 35.5749 9.742 35.4669 9.994L32.5509 16.96C32.4069 17.32 32.1189 17.5 31.7409 17.5H30.8409ZM40.8852 17.68C37.9692 17.68 36.0432 15.772 36.0432 12.91C36.0432 10.012 38.0412 8.14 40.9932 8.14C43.2072 8.14 44.8092 9.166 44.8092 10.642C44.8092 12.442 42.5412 13.162 40.1652 13.306C40.5252 14.314 41.2092 15.178 42.3612 15.178C43.0092 15.178 43.4232 14.89 43.7832 14.638C44.0892 14.422 44.2872 14.314 44.5032 14.314C44.7552 14.314 44.9532 14.458 44.9532 14.926C44.9532 16.474 43.2072 17.68 40.8852 17.68ZM39.7872 11.002C39.7872 11.398 39.8232 11.902 39.9312 12.406C41.1192 12.244 41.5332 11.542 41.5332 10.678C41.5332 9.868 41.1912 9.13 40.6332 9.13C39.9672 9.13 39.7872 10.12 39.7872 11.002ZM47.7301 17.698C46.4881 17.698 45.6781 16.906 45.6781 15.736C45.6781 14.548 46.4881 13.774 47.7301 13.774C48.9541 13.774 49.7641 14.548 49.7641 15.736C49.7641 16.906 48.9541 17.698 47.7301 17.698Z" />
  </svg>
);

/**
 * Google brand logo. Use for SSO buttons and integration cards.
 *
 * Defaults to `size-5` (20px). Renders in Google's official brand colors;
 * pass `className` for sizing or layout utilities only, do not retint.
 *
 * @example
 * <GoogleLogo className="size-6 shrink-0" />
 */
export const GoogleLogo: FC<LogoProps> = ({ className }) => (
  <svg
    aria-hidden="true"
    className={cn('size-5', className)}
    viewBox="0 0 24 24"
  >
    <path
      d="M12.0003 4.75C13.7703 4.75 15.3553 5.36002 16.6053 6.54998L20.0303 3.125C17.9502 1.19 15.2353 0 12.0003 0C7.31028 0 3.25527 2.69 1.28027 6.60998L5.27028 9.70498C6.21525 6.86002 8.87028 4.75 12.0003 4.75Z"
      fill="#EA4335"
    />
    <path
      d="M23.49 12.275C23.49 11.49 23.415 10.73 23.3 10H12V14.51H18.47C18.18 15.99 17.34 17.25 16.08 18.1L19.945 21.1C22.2 19.01 23.49 15.92 23.49 12.275Z"
      fill="#4285F4"
    />
    <path
      d="M5.26498 14.2949C5.02498 13.5699 4.88501 12.7999 4.88501 11.9999C4.88501 11.1999 5.01998 10.4299 5.26498 9.7049L1.275 6.60986C0.46 8.22986 0 10.0599 0 11.9999C0 13.9399 0.46 15.7699 1.28 17.3899L5.26498 14.2949Z"
      fill="#FBBC05"
    />
    <path
      d="M12.0004 24.0001C15.2404 24.0001 17.9654 22.935 19.9454 21.095L16.0804 18.095C15.0054 18.82 13.6204 19.245 12.0004 19.245C8.8704 19.245 6.21537 17.135 5.2654 14.29L1.27539 17.385C3.25539 21.31 7.3104 24.0001 12.0004 24.0001Z"
      fill="#34A853"
    />
  </svg>
);

/**
 * Linear brand logo. Use for Linear-specific affordances such as the
 * reviewer dash "create ticket" button.
 *
 * Defaults to `size-5` (20px). Renders in Linear's official brand color;
 * pass `className` for sizing or layout utilities only, do not retint.
 *
 * @example
 * <LinearLogo className="size-8" />
 */
export const LinearLogo: FC<LogoProps> = ({ className }) => (
  <svg
    aria-hidden="true"
    className={cn('size-5', className)}
    viewBox="0 0 24 24"
  >
    <path
      d="M3.03509 12.9431C3.24245 14.9227 4.10472 16.8468 5.62188 18.364C7.13904 19.8811 9.0631 20.7434 11.0428 20.9508L3.03509 12.9431Z"
      fill="#000000"
    />
    <path
      d="M3 11.4938L12.4921 20.9858C13.2976 20.9407 14.0981 20.7879 14.8704 20.5273L3.4585 9.11548C3.19793 9.88771 3.0451 10.6883 3 11.4938Z"
      fill="#000000"
    />
    <path
      d="M3.86722 8.10999L15.8758 20.1186C16.4988 19.8201 17.0946 19.4458 17.6493 18.9956L4.99021 6.33659C4.54006 6.89125 4.16573 7.487 3.86722 8.10999Z"
      fill="#000000"
    />
    <path
      d="M5.66301 5.59517C9.18091 2.12137 14.8488 2.135 18.3498 5.63604C21.8508 9.13708 21.8645 14.8049 18.3907 18.3228L5.66301 5.59517Z"
      fill="#000000"
    />
  </svg>
);

/**
 * Microsoft brand logo. Use for SSO buttons and integration cards.
 *
 * Defaults to `size-5` (20px). Renders in Microsoft's official brand colors;
 * pass `className` for sizing or layout utilities only, do not retint.
 * Includes a `<title>` element for screen readers.
 *
 * @example
 * <MicrosoftLogo className="size-5 shrink-0" />
 */
export const MicrosoftLogo: FC<LogoProps> = ({ className }) => (
  <svg
    className={cn('size-5', className)}
    viewBox="0 0 32 32"
    xmlns="http://www.w3.org/2000/svg"
  >
    <title>Microsoft Logo</title>
    <rect x="2" y="2" width="12" height="12" fill="#f25022" />
    <rect x="2" y="16" width="12" height="12" fill="#00a4ef" />
    <rect x="16" y="2" width="12" height="12" fill="#7fba00" />
    <rect x="16" y="16" width="12" height="12" fill="#ffb900" />
  </svg>
);
